/**
 * The timeline filtered to recordings made while their unit was inside an
 * area drawn on the map (design §9).
 *
 * @module
 */
import type { TimelineLane } from "../api/client";
import type { Interval } from "./tracks";

/** `library/unit` of a source named `unit/…`, which every source of that unit shares. */
export function unitKey(libraryId: string, source: string): string {
  return `${libraryId}/${source.split("/")[0] ?? source}`;
}

/** Intervals per GPS source (`library/source`) regrouped per unit. */
export function byUnit(
  intervals: ReadonlyMap<string, Interval[]>,
): Map<string, Interval[]> {
  const out = new Map<string, Interval[]>();
  for (const [key, list] of intervals) {
    const [library = "", ...rest] = key.split("/");
    const unit = unitKey(library, rest.join("/"));
    out.set(unit, [...(out.get(unit) ?? []), ...list]);
  }
  return out;
}

/** Lanes with only files overlapping their unit's intervals; lanes left empty are dropped. */
export function filterLanes(
  lanes: readonly TimelineLane[],
  units: ReadonlyMap<string, Interval[]>,
): TimelineLane[] {
  return lanes.flatMap((l) => {
    const within = l.source
      ? units.get(unitKey(l.library_id, l.source))
      : undefined;
    if (!within) return [];
    const files = l.files.filter((f) => {
      const from = Date.parse(f.start_utc);
      const to = f.end_utc ? Date.parse(f.end_utc) : from;
      return within.some((i) => from <= i.to && to >= i.from);
    });
    return files.length > 0 ? [{ ...l, files }] : [];
  });
}
